import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  AlertTriangle, CheckCircle2, Camera, Zap, Radio, BarChart3,
  Trophy, Users, TrendingUp, DollarSign, Calendar, ArrowRight,
  Download, Loader2, X, Check,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LeaguePartnersPage() {
  const [form, setForm] = useState({
    league_name: '',
    contact_name: '',
    email: '',
    phone: '',
    teams_estimate: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.league_name || !form.contact_name || !form.email) {
      toast.error('League name, your name, and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/league-partner/contact', form);
      setSubmitted(true);
      toast.success("Got it! We'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again?');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950" data-testid="league-partners-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-950 via-green-900 to-stone-900" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(251,191,36,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(251,191,36,0.3) 0%, transparent 40%)',
        }} />
        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
          <Link to="/" className="inline-flex items-center text-amber-300 hover:text-amber-200 text-sm mb-8 font-semibold">
            <X size={14} className="mr-1 rotate-45" /> Back to diamondscorer.com
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 text-xs font-bold uppercase tracking-widest mb-6">
            <Trophy size={12} /> For League Commissioners
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[0.95] mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            BRING DIAMOND SCORER<br />
            <span className="text-amber-400">TO YOUR WHOLE LEAGUE.</span>
          </h1>
          <p className="text-green-100 text-lg md:text-xl max-w-2xl leading-relaxed mb-8">
            The scoring & family-engagement app your coaches wish they&apos;d had years ago —
            rolled out league-wide with dedicated support, bulk pricing, and your logo on every scoreboard.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#contact">
              <Button className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold text-base h-12 px-6" data-testid="hero-contact-btn">
                Start the conversation <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
            <a href={`${BACKEND}/league_pitch_deck.pdf`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-amber-500/50 text-amber-200 hover:bg-amber-500/10 h-12 px-6" data-testid="hero-pdf-btn">
                <Download size={16} className="mr-2" /> Full proposal (PDF)
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ============ THE PROBLEM ============ */}
      <section className="bg-stone-100 py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <div className="text-red-600 text-xs font-bold uppercase tracking-widest mb-2">The problem</div>
            <h2 className="text-4xl md:text-5xl font-black text-stone-900 leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              LITTLE LEAGUES RUN ON PAPER<br />
              <span className="text-red-600">AND TRIBAL KNOWLEDGE.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                title: "Scorebooks don't scale",
                body: "No league-wide stats. No easy way to compare teams. No digital record of the season — just a binder the manager promises to bring to the banquet.",
              },
              {
                title: 'Parents feel disconnected',
                body: 'A parent stuck at work has no idea what happened. They find out from a "nice game!" text 3 hours later. The family connection fades.',
              },
              {
                title: 'Every coach reinvents the wheel',
                body: "Every coach learns their own quirky system. Assistants can't fill in. When the coach quits mid-season, the data is gone.",
              },
            ].map((p, i) => (
              <Card key={i} className="border-0 shadow-md overflow-hidden" data-testid={`problem-card-${i}`}>
                <div className="h-1 bg-red-600" />
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle size={16} className="text-red-600" />
                    </div>
                    <h3 className="font-bold text-stone-900 text-lg leading-tight pt-1">{p.title}</h3>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed pl-11">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE SOLUTION ============ */}
      <section className="bg-white py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <div className="text-green-700 text-xs font-bold uppercase tracking-widest mb-2">The solution</div>
            <h2 className="text-4xl md:text-5xl font-black text-stone-900 leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              ONE APP. EVERY COACH.<br />
              <span className="text-green-700">EVERY PARENT. EVERY GRANDPARENT.</span>
            </h2>
            <p className="text-stone-600 mt-4 text-lg max-w-3xl">
              Diamond Scorer is built from the ground up for volunteer coaches. They already know how to write a lineup
              card. They don&apos;t need training.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: Camera, title: 'AI LINEUP SCAN', body: 'Write the lineup on paper. Snap a photo. Names and numbers auto-fill in 3 seconds. No typing. No apps to learn.' },
              { icon: Zap, title: 'TAP TO SCORE', body: 'Big BALL / STRIKE / HIT / OUT buttons. Auto-counts outs, auto-advances innings, auto-calculates stats. Even first-time coaches score confidently.' },
              { icon: Radio, title: 'LIVE FAMILY FEED', body: 'Parents, grandparents, aunts, uncles — all follow every pitch live. No app store download. A link in the group text and they\'re in.' },
              { icon: BarChart3, title: 'SEASON STATS AUTO-BUILD', body: 'Batting averages, OPS, pitch counts, spray charts. Exportable to CSV. Coaches hand over clean data at season end — no lost binders.' },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <Card key={i} className="border-0 shadow-md hover:shadow-xl transition-shadow overflow-hidden" data-testid={`feature-card-${i}`}>
                  <div className="h-1 bg-green-700" />
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-green-700 flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-stone-900 text-xl leading-tight mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{f.title}</h3>
                        <p className="text-stone-600 text-sm leading-relaxed">{f.body}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ VALUE PROPS ============ */}
      <section className="bg-green-950 py-16 md:py-24 px-6 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <div className="text-amber-300 text-xs font-bold uppercase tracking-widest mb-2">Why league-wide beats team-by-team</div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              WHAT THE LEAGUE GETS.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: TrendingUp, title: 'Unified league data', body: 'Every game, every team, every season in one place. Pull any season\'s stats with one click. Your banquet awards night writes itself.' },
              { icon: Users, title: 'Volunteer coach retention', body: 'New coaches inherit the roster, stats, and message history automatically. No more "Help, I lost the scorebook!" emergencies.' },
              { icon: Calendar, title: 'Family engagement = sign-up retention', body: 'Leagues with engaged families have higher year-over-year sign-ups. When grandparents follow games from anywhere, re-registration is a no-brainer.' },
              { icon: DollarSign, title: 'Sponsor opportunity', body: 'Every family in your league sees Diamond Scorer every game. Add a sponsor logo to your league scoreboards — that\'s a new revenue stream.' },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-green-900/40 border border-green-700/40" data-testid={`value-prop-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-green-950" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-300 text-lg mb-1">{v.title}</h3>
                    <p className="text-green-100 text-sm leading-relaxed">{v.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ COMPARISON TABLE ============ */}
      <section className="bg-stone-100 py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10 text-center">
            <div className="text-green-700 text-xs font-bold uppercase tracking-widest mb-2">Heads-up comparison</div>
            <h2 className="text-4xl md:text-5xl font-black text-stone-900 leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              DIAMOND SCORER <span className="text-stone-400">vs</span> GAMECHANGER
            </h2>
          </div>
          <div className="overflow-hidden rounded-xl shadow-xl border border-stone-200">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-green-950 text-white">
                  <th className="px-4 py-3 text-left font-bold">Feature</th>
                  <th className="px-4 py-3 text-center font-bold">
                    <span className="text-amber-300">Diamond Scorer</span>
                  </th>
                  <th className="px-4 py-3 text-center font-bold text-stone-400">GameChanger</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {[
                  ['No app store download for parents', 'YES — browser link', 'NO — app required'],
                  ['AI lineup card scan', 'YES', 'NO'],
                  ['Personal highlight reel per kid', 'YES — shareable link', 'Limited'],
                  ['League-wide stats dashboard', 'On roadmap', 'Extra cost'],
                  ['Starting price for coaches', 'Free trial, then $/month', 'Higher tier'],
                  ['Support email response time', 'Within 1 business day', 'Varies'],
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-4 py-3 font-semibold text-stone-800">{row[0]}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 text-green-700 font-bold">
                        <Check size={14} /> {row[1]}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-stone-500">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============ ROLLOUT ============ */}
      <section className="bg-white py-16 md:py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <div className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-2">How to get started</div>
            <h2 className="text-4xl md:text-5xl font-black text-stone-900 leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              NO BIG SIGNATURE.<br />
              <span className="text-green-700">JUST A LOW-RISK PILOT.</span>
            </h2>
          </div>
          <div className="space-y-4">
            {[
              { n: 1, title: 'Free pilot with 2-3 teams', body: 'Pick 2-3 volunteer coaches this season. They use Diamond Scorer free for the full season. You see real results before committing league-wide.' },
              { n: 2, title: 'Collect feedback', body: 'End of season, a 10-minute conversation with each pilot coach tells you if families engaged, if the data was useful, and if coaches want to keep using it.' },
              { n: 3, title: 'League partnership next season', body: 'If the pilot works, we set up bulk coach accounts, your league logo on all scoreboards, a league admin dashboard, and a dedicated support line.' },
              { n: 4, title: 'Announce at opening day', body: 'Your league kicks off the new season with a league-wide app that parents love and coaches already know. Re-registration rates tend to climb in year 2.' },
            ].map((s, i) => (
              <div key={i} className="flex gap-5 items-start p-5 rounded-xl border border-stone-200 bg-stone-50" data-testid={`rollout-step-${i}`}>
                <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 font-black text-2xl text-green-950" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {s.n}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-stone-900 text-xl mb-1">{s.title}</h3>
                  <p className="text-stone-600 text-base leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CONTACT FORM ============ */}
      <section id="contact" className="bg-gradient-to-br from-green-900 to-green-950 py-16 md:py-24 px-6 text-white">
        <div className="max-w-2xl mx-auto">
          <div className="mb-10 text-center">
            <div className="text-amber-300 text-xs font-bold uppercase tracking-widest mb-2">Ready to talk?</div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              LET&apos;S SET UP A<br />
              <span className="text-amber-400">20-MINUTE CALL.</span>
            </h2>
            <p className="text-green-100 text-base">
              No contracts. No commitments. Just a walkthrough + answers to your questions.
            </p>
          </div>

          {submitted ? (
            <Card className="border-0 shadow-2xl" data-testid="contact-success">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 size={32} className="text-green-700" />
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-2">Got it, {form.contact_name.split(' ')[0] || 'Coach'}!</h3>
                <p className="text-stone-600 mb-6">
                  We just sent a confirmation to <strong>{form.email}</strong>. Expect a personal reply within 1 business day.
                </p>
                <div className="flex flex-col gap-2 max-w-xs mx-auto">
                  <a href={`${BACKEND}/league_pitch_deck.pdf`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="w-full" data-testid="success-download-pitch">
                      <Download size={14} className="mr-2" /> Download full pitch deck
                    </Button>
                  </a>
                  <Link to="/">
                    <Button variant="ghost" className="w-full text-stone-500" data-testid="success-back-home">
                      Back to Diamond Scorer
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="league-contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="league_name" className="text-stone-900 font-bold text-xs uppercase tracking-wider">League name *</Label>
                      <Input
                        id="league_name"
                        required
                        value={form.league_name}
                        onChange={handleChange('league_name')}
                        placeholder="e.g., West Akron Little League"
                        className="bg-white mt-1"
                        data-testid="league-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="teams_estimate" className="text-stone-900 font-bold text-xs uppercase tracking-wider">Approx. # of teams</Label>
                      <Input
                        id="teams_estimate"
                        value={form.teams_estimate}
                        onChange={handleChange('teams_estimate')}
                        placeholder="e.g., 24"
                        className="bg-white mt-1"
                        data-testid="teams-estimate-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact_name" className="text-stone-900 font-bold text-xs uppercase tracking-wider">Your name *</Label>
                    <Input
                      id="contact_name"
                      required
                      value={form.contact_name}
                      onChange={handleChange('contact_name')}
                      placeholder="Jane Smith"
                      className="bg-white mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-stone-900 font-bold text-xs uppercase tracking-wider">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange('email')}
                        placeholder="jane@mylittleleague.org"
                        className="bg-white mt-1"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-stone-900 font-bold text-xs uppercase tracking-wider">Phone (optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange('phone')}
                        placeholder="(555) 123-4567"
                        className="bg-white mt-1"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-stone-900 font-bold text-xs uppercase tracking-wider">Anything else we should know?</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={handleChange('message')}
                      placeholder="How many seasons, special needs, sponsor interest, etc."
                      rows={4}
                      className="bg-white mt-1 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold text-base"
                    data-testid="submit-league-contact"
                  >
                    {submitting ? (
                      <><Loader2 size={18} className="mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <>Send <ArrowRight size={18} className="ml-2" /></>
                    )}
                  </Button>
                  <p className="text-[11px] text-stone-500 text-center pt-1">
                    We&apos;ll reply within 1 business day. Zero spam. Unsubscribe anytime.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-stone-950 py-8 px-6 border-t border-stone-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="text-stone-400">
            <span className="font-bold text-white">DIAMOND</span>
            <span className="font-bold text-amber-400 italic"> SCORER</span>
            <span className="ml-3 text-stone-500">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-5 text-stone-400">
            <a href="mailto:diamondscorer@outlook.com" className="hover:text-amber-300 transition-colors">
              diamondscorer@outlook.com
            </a>
            <Link to="/" className="hover:text-amber-300 transition-colors">diamondscorer.com</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
